<!--
  登录弹窗组件
  从Header.vue中拆分出来的登录功能
-->
<template>
  <el-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :title="loginMode === 'email' ? (isLoginMode ? '登录您的账户' : '注册新账户') : '微信扫码登录'"
    width="400px"
    :close-on-click-modal="false"
    align-center
  >
    <div class="login-dialog-content">
      <div class="dialog-logo">
        <div class="logo-text">文字转语音</div>
      </div>

      <!-- 登录方式切换 -->
      <div class="login-mode-tabs">
        <el-button
          :type="loginMode === 'wechat' ? 'primary' : ''"
          @click="switchLoginMode('wechat')"
          size="small"
        >
          微信扫码，立即登录
        </el-button>
        <el-button
          :type="loginMode === 'email' ? 'primary' : ''"
          @click="switchLoginMode('email')"
          size="small"
        >
          账号登录
        </el-button>
      </div>

      <!-- 微信扫码登录 -->
      <div v-if="loginMode === 'wechat'" class="wechat-login-container">
        <WechatLogin
          :on-success="handleWechatLoginSuccess"
          :on-error="handleWechatLoginError"
          @switch-to-email="switchLoginMode('email')"
          ref="wechatLoginRef"
        />
        <div class="login-mode-switch">
          <el-text size="small" type="info">
            绑定邮箱后可使用账号登录
          </el-text>
        </div>
      </div>

      <!-- 邮箱登录表单 -->
      <el-form
        v-else-if="loginMode === 'email' && isLoginMode"
        id="loginForm"
        ref="loginFormRef"
        :model="loginForm"
        :rules="loginRules"
        label-position="top"
      >
        <el-form-item label="账号" prop="email">
          <el-input
            v-model="loginForm.email"
            placeholder="请输入电子邮箱/用户名"
          />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input
            v-model="loginForm.password"
            type="password"
            placeholder="请输入密码"
            show-password
          />
        </el-form-item>

        <!-- 错误提示区域 -->
        <div v-if="loginError" class="login-error-message">
          <el-alert
            :title="loginError"
            type="error"
            :closable="false"
            show-icon
          />
        </div>

        <div class="form-actions">
          <el-button type="primary" :loading="loading" @click="handleLogin" style="width: 100%">
            立即登录
          </el-button>
        </div>
      </el-form>

      <!-- 注册表单 - 已禁用，只能通过微信登录注册 -->
      <el-form
        v-else-if="false"
        ref="registerFormRef"
        :model="registerForm"
        :rules="registerRules"
        label-position="top"
        autocomplete="off"
      >
        <el-form-item label="用户名" prop="username">
          <el-input
            v-model="registerForm.username"
            placeholder="请输入用户名"
            autocomplete="off"
            name="register-username"
          />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input
            v-model="registerForm.email"
            placeholder="请输入电子邮箱"
            autocomplete="off"
            name="register-email"
          >
            <template #append>
              <el-button
                :disabled="isSendCodeDisabled"
                @click="handleSendCode"
                size="small"
              >
                {{ countdown ? `${countdown}s` : '获取验证码' }}
              </el-button>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item label="验证码" prop="verificationCode">
          <el-input
            v-model="registerForm.verificationCode"
            placeholder="请输入验证码"
            autocomplete="off"
            name="verification-code"
          />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input
            v-model="registerForm.password"
            type="password"
            placeholder="请输入密码"
            show-password
            autocomplete="new-password"
            name="register-password"
          />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input
            v-model="registerForm.confirmPassword"
            type="password"
            placeholder="请再次输入密码"
            show-password
            autocomplete="new-password"
            name="register-confirm-password"
          />
        </el-form-item>

        <!-- 错误提示区域 -->
        <div v-if="registerError" class="login-error-message">
          <el-alert
            :title="registerError"
            type="error"
            :closable="false"
            show-icon
          />
        </div>

        <div class="form-actions">
          <el-button type="primary" :loading="loading" @click="handleRegister" style="width: 100%">
            立即注册
          </el-button>
        </div>
      </el-form>

      <!-- 切换登录/注册模式 - 已禁用邮箱注册，只显示微信登录提示 -->
      <div class="mode-switch" v-if="loginMode === 'email'">
        <el-button type="text" @click="switchLoginMode('wechat')">
          没有账户？使用微信登录注册
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/store'
import WechatLogin from '@/views/auth/WechatLogin.vue'

// Props
interface Props {
  modelValue: boolean
}

const props = defineProps<Props>()

// Emits
interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'login-success', data: any): void
  (e: 'register-success', data: any): void
}

const emit = defineEmits<Emits>()

// Store
const userStore = useUserStore()

// 状态管理
const loginMode = ref<'email' | 'wechat'>('wechat') // 默认使用微信登录
const isLoginMode = ref(true)
const loading = ref(false)
const loginError = ref('')
const registerError = ref('')

// 表单引用
const loginFormRef = ref<any>()
const registerFormRef = ref<any>()
const wechatLoginRef = ref<any>()

// 登录表单
const loginForm = reactive({
  email: '',
  password: ''
})

// 注册表单
const registerForm = reactive({
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  verificationCode: ''
})

// 验证码倒计时
const countdown = ref(0)

// 计算属性：邮箱是否有效
const isEmailValid = computed(() => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailPattern.test(registerForm.email)
})

// 计算属性：发送验证码按钮是否禁用
const isSendCodeDisabled = computed(() => {
  return !!countdown.value || loading.value || !isEmailValid.value
})

// 表单验证规则
const loginRules = {
  email: [
    { required: true, message: '请输入邮箱或用户名', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' }
  ]
}

const registerRules: any = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' },
    { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
  ],
  email: [
    { required: true, message: '请输入邮箱', trigger: 'blur' },
    { type: 'email' as const, message: '请输入正确的邮箱格式', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: '请确认密码', trigger: 'blur' },
    {
      validator: (_rule: any, value: string, callback: Function) => {
        if (value !== registerForm.password) {
          callback(new Error('两次输入密码不一致'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ],
  verificationCode: [
    { required: true, message: '请输入验证码', trigger: 'blur' }
  ]
} as const

// 方法
const switchLoginMode = (mode: 'email' | 'wechat') => {
  // 如果从微信登录切换到邮箱登录，停止微信登录的轮询
  if (loginMode.value === 'wechat' && mode === 'email') {
    if (wechatLoginRef.value) {
      wechatLoginRef.value.cleanup?.()
    }
  }

  loginMode.value = mode
  clearErrors()
}

const clearErrors = () => {
  loginError.value = ''
  registerError.value = ''
}

// 发送验证码
const handleSendCode = async () => {
  try {
    if (!registerForm.email) {
      ElMessage.warning('请先输入邮箱')
      return
    }

    loading.value = true
    await userStore.sendVerificationCode(registerForm.email)

    // 如果没有抛出异常，说明发送成功
    startCountdown()
  } catch (error: any) {
    // store中已经处理了错误显示，这里不需要重复显示
    console.error('发送验证码失败:', error)
  } finally {
    loading.value = false
  }
}

// 开始倒计时
const startCountdown = () => {
  countdown.value = 60
  const timer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(timer)
    }
  }, 1000)
}

const handleLogin = async () => {
  if (!loginFormRef.value) return

  try {
    await loginFormRef.value.validate()
    loading.value = true
    loginError.value = ''

    // 调用实际的登录API
    await userStore.login({
      email: loginForm.email,
      password: loginForm.password
    })

    // 登录成功，停止微信登录的轮询（如果有）
    if (wechatLoginRef.value) {
      wechatLoginRef.value.cleanup?.()
    }

    // 如果没有抛出异常，说明登录成功
    emit('login-success', { ...loginForm })
    emit('update:modelValue', false)
  } catch (error: any) {
    // 处理未绑定邮箱的错误
    const errorMsg = error.message || '登录失败，请检查账号密码'

    if (errorMsg.includes('未绑定邮箱') || errorMsg.includes('邮箱未绑定')) {
      loginError.value = '该账号未绑定邮箱，请使用微信登录后在账户设置中绑定邮箱'
    } else {
      loginError.value = errorMsg
    }
  } finally {
    loading.value = false
  }
}

const handleRegister = async () => {
  if (!registerFormRef.value) return

  try {
    await registerFormRef.value.validate()
    loading.value = true
    registerError.value = ''

    // 调用实际的注册API
    await userStore.register({
      username: registerForm.username,
      email: registerForm.email,
      password: registerForm.password,
      verificationCode: registerForm.verificationCode
    })

    // 如果没有抛出异常，说明注册成功
    ElMessage.success('注册成功！请登录')

    // 切换到登录模式
    isLoginMode.value = true

    // 清空注册表单
    registerForm.username = ''
    registerForm.email = ''
    registerForm.password = ''
    registerForm.confirmPassword = ''
    registerForm.verificationCode = ''

    // 触发注册成功事件
    emit('register-success', { ...registerForm })
  } catch (error: any) {
    registerError.value = error.message || '注册失败，请重试'
  } finally {
    loading.value = false
  }
}

const handleWechatLoginSuccess = async (data: any) => {
  try {
    // 调用 userStore 的微信登录处理方法
    await userStore.handleWechatLoginSuccess(data)

    ElMessage.success('微信登录成功')
    emit('login-success', data)
    emit('update:modelValue', false)
  } catch (error) {
    console.error('微信登录后数据刷新失败:', error)
    // 即使刷新失败，也让用户登录成功
    ElMessage.success('微信登录成功')
    emit('login-success', data)
    emit('update:modelValue', false)
  }
}

const handleWechatLoginError = (error: any) => {
  ElMessage.error('微信登录失败')
  console.error('Wechat login error:', error)
}

// 监听对话框打开状态，重置微信登录组件
watch(() => props.modelValue, async (newValue) => {
  if (newValue && loginMode.value === 'wechat') {
    // 对话框打开且是微信登录模式，等待DOM更新后重置微信登录组件
    await nextTick()
    if (wechatLoginRef.value) {
      wechatLoginRef.value.reset?.()
    }
  } else if (!newValue && loginMode.value === 'wechat') {
    // 对话框关闭时，如果当前是微信登录模式，停止微信登录的轮询
    // 使用 nextTick 确保在组件销毁前调用
    await nextTick()
    if (wechatLoginRef.value) {
      wechatLoginRef.value.cleanup?.()
    }
  }
})
</script>

<style scoped>
.login-dialog-content {
  padding: 20px 0;
}

.dialog-logo {
  text-align: center;
  margin-bottom: 30px;
}

.logo-text {
  font-size: 24px;
  font-weight: bold;
  color: var(--el-color-primary);
}

.login-mode-tabs {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;
}

.wechat-login-container {
  text-align: center;
}

.login-mode-switch {
  margin-top: 20px;
}

.login-error-message {
  margin-bottom: 15px;
}

.form-actions {
  margin-top: 20px;
}

.mode-switch {
  text-align: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--el-border-color-light);
}
</style>
